import { CloudAnomalyAlert } from "../../types/alert";
import { Badge } from "../../../../ui/common-ui/badge";
import { Card, CardContent, CardHeader } from "../../../../ui/common-ui/card";
import { Button } from "../../../../ui/common-ui/button";
import { AlertTriangle, Shield, Clock, User, ExternalLink, Brain, BookOpen, Route } from "lucide-react";
import { format } from "date-fns";

interface AlertCardProps {
  alert: CloudAnomalyAlert;
  onViewDetails: (alert: CloudAnomalyAlert) => void;
}

const getSeverityConfig = (severity: string) => {
  const normalized = severity.toLowerCase();
  const configs: Record<string, { className: string; icon: typeof AlertTriangle; label: string; borderColor: string }> = {
    critical: { className: "severity-critical", icon: AlertTriangle, label: "Critical", borderColor: "border-l-red-500" },
    high: { className: "severity-high", icon: AlertTriangle, label: "High", borderColor: "border-l-orange-500" },
    medium: { className: "severity-medium", icon: AlertTriangle, label: "Medium", borderColor: "border-l-yellow-500" },
  };
  return configs[normalized] || configs.medium;
};

const getAlertTypeConfig = (alertType: string) => {
  const configs = {
    BEHAVIOUR: { icon: Brain, label: "Behaviour", className: "bg-purple-100 text-purple-700 border-purple-300" },
    RULE: { icon: BookOpen, label: "Rule", className: "bg-blue-100 text-blue-700 border-blue-300" },
    SEQUENTIAL: { icon: Route, label: "Sequential", className: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  };
  return configs[alertType as keyof typeof configs] || configs.RULE;
};

export const AlertCard = ({ alert, onViewDetails }: AlertCardProps) => {
  const severityConfig = getSeverityConfig(alert.severity);
  const alertTypeConfig = getAlertTypeConfig(alert.alertType);
  const SeverityIcon = severityConfig.icon;
  const AlertTypeIcon = alertTypeConfig.icon;

  const attackPathSteps = Object.keys(alert.attackPath).length;
  const mitreEntries = Object.entries(alert.mitre);
  const isSequential = alert.alertType === "SEQUENTIAL";

  return (
    <Card className={`bg-card rounded-2xl hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] hover:-translate-y-px hover:border-primary/30 transition-all duration-200 overflow-hidden border border-border/50 border-l-[4px] ${severityConfig.borderColor} shadow-lg shadow-sky-500/5`}>
      <CardHeader className="p-5 pb-3">
        {/* Alert Type & Severity Badges */}
        <div className="flex items-center gap-2 mb-3">
          <Badge variant={isSequential ? "default" : "outline"} className={alertTypeConfig.className}>
            <AlertTypeIcon className="h-3 w-3 mr-1.5" />
            {alertTypeConfig.label}
          </Badge>
          <Badge
            variant="outline"
            className={`${severityConfig.className} w-fit font-medium`}
          >
            <SeverityIcon className="h-3 w-3 mr-1.5" />
            {severityConfig.label}
          </Badge>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-[16px] text-foreground leading-tight mb-1 line-clamp-2" title={alert.title}>
          {alert.title}
        </h3>
      </CardHeader>

      <CardContent className="p-5 pt-0 space-y-3">
        {/* Entity & Cloud */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-foreground font-medium truncate" title={alert.entity}>
              {alert.entity}
            </span>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            <div className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              <span className="truncate max-w-[60px]">{alert.cloudType}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 flex-shrink-0" />
              <span className="whitespace-nowrap">{format(new Date(alert.createdAt), "MMM dd, HH:mm")}</span>
            </div>
          </div>
        </div>

        {/* Attack Path Preview for RULE and SEQUENTIAL */}
        {attackPathSteps > 0 && (
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                {isSequential ? "Attack Chain" : "Attack Step"}
              </span>
              <Badge variant="secondary" className="text-xs">
                {attackPathSteps} {attackPathSteps === 1 ? "Step" : "Steps"}
              </Badge>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {Object.entries(alert.attackPath).slice(0, 3).map(([step, data], idx) => (
                <div key={step} className="flex items-center gap-1.5">
                  <span className="font-mono bg-background px-1.5 py-0.5 rounded truncate max-w-[80px]">
                    {data.action.split('.').pop()}
                  </span>
                  {idx < Math.min(attackPathSteps - 1, 2) && (
                    <span className="text-primary">→</span>
                  )}
                </div>
              ))}
              {attackPathSteps > 3 && (
                <span className="font-semibold">+{attackPathSteps - 3}</span>
              )}
            </div>
          </div>
        )}

        {/* Description */}
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2" title={alert.info.what}>
          {alert.info.what || "No description available"}
        </p>

        {/* MITRE Tags */}
        {mitreEntries.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2">
            {mitreEntries.slice(0, 2).map(([code, name]) => (
              <Badge
                key={code}
                variant="secondary"
                className="text-xs font-mono"
                title={name}
              >
                {code}
              </Badge>
            ))}
            {mitreEntries.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{mitreEntries.length - 2}
              </Badge>
            )}
          </div>
        )}

        {/* Action Button */}
        <div
          onClick={() => onViewDetails(alert)}
          className="mt-2 p-2.5 rounded-md border border-border/50 bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors flex items-center justify-between"
        >
          <span className="text-xs text-primary font-medium">
            {isSequential ? "View Attack Timeline" : "View Details"}
          </span>
          <ExternalLink className="h-3.5 w-3.5 text-primary" />
        </div>
      </CardContent>
    </Card>
  );
};
